import { FC, useState } from 'react';
import { Button, Checkbox } from '@spotted-zebra-uk/ui-components';
import styles from './ConsentActions.module.scss';

export interface IConsentActionsProps {
  loading: boolean;
  checkboxLabel: string;
  /** Shown under the box when Confirm is pressed without ticking it. */
  errorText: string;
  /** Decline renders only when `onDecline` is given. */
  declineLabel?: string;
  confirmLabel: string;
  /** Test ids: `${idPrefix}__checkbox-input`, `${idPrefix}__decline`, `${idPrefix}__confirm`; the checkbox id is the prefix. */
  idPrefix: string;
  onConfirm: () => void;
  onDecline?: () => void;
}

/**
 * A consent checkbox with Confirm and an optional Decline button. Confirm is
 * always enabled, but only goes through once the box is ticked; Decline
 * never needs the box.
 */
const ConsentActions: FC<IConsentActionsProps> = ({
  loading,
  checkboxLabel,
  errorText,
  declineLabel,
  confirmLabel,
  idPrefix,
  onConfirm,
  onDecline,
}) => {
  const [consented, setConsented] = useState(false);
  const [hasError, setHasError] = useState(false);

  const handleConfirm = () => {
    if (!consented) {
      setHasError(true);
      return;
    }
    onConfirm();
  };

  return (
    <div className={styles.consent}>
      <Checkbox
        id={idPrefix}
        checked={consented}
        disabled={loading}
        label={checkboxLabel}
        hasError={hasError}
        bottomText={hasError ? errorText : undefined}
        bottomTextAriaLive="assertive"
        ariaRequired
        dataTestid={`${idPrefix}__checkbox`}
        onChange={() => {
          setConsented(prev => !prev);
          setHasError(false);
        }}
      />
      <div className={styles.buttons}>
        {onDecline && (
          <Button
            variant="secondary"
            className={styles.decline}
            fullWidth
            disabled={loading}
            onClick={onDecline}
            data-testid={`${idPrefix}__decline`}
          >
            {declineLabel}
          </Button>
        )}
        <Button
          fullWidth
          loading={loading}
          disabled={loading}
          onClick={handleConfirm}
          data-testid={`${idPrefix}__confirm`}
        >
          {confirmLabel}
        </Button>
      </div>
    </div>
  );
};

export default ConsentActions;
